'use client';

import Header from '@/components/common/Header';
import Footer from '@/app/homepage/components/Footer';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';
import Link from 'next/link';
import styles from '@/app/homepage/styles/home2.module.css';

interface Author {
  name: string;
  image: string;
  alt: string;
  bio?: string;
}

interface BlogPost {
  id: number;
  slug: string;
  title: string;
  excerpt: string;
  category: string;
  author: Author;
  publishDate: string;
  readTime: string;
  image: string;
  alt: string;
  tags: string[];
  content?: string;
  images?: { url: string; alt: string }[];
  faqs?: { question: string; answer: string }[];
}

interface BlogArticleProps {
  post: BlogPost;
}

const BlogArticle = ({ post }: BlogArticleProps) => {
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  };

  // Convert content string to JSX
  const renderContent = () => {
    return { __html: post.content ?? '' };
  };

  const canonicalUrl = `https://www.hayatinteriors.com/blog/${post.slug}`;

  const localBusiness = {
    '@type': 'LocalBusiness',
    name: 'Hayat Interiors',
    url: 'https://www.hayatinteriors.com',
    telephone: '+91 98862 76722',
    address: {
      '@type': 'PostalAddress',
      streetAddress:
        'First Floor, 513/C, 4th Block, above Nilambur Furniture, 1st Stage, Telecom Layout, HBR Layout',
      addressLocality: 'Bangalore',
      addressRegion: 'Karnataka',
      postalCode: '560043',
      addressCountry: 'IN',
    },
  };

  // Create structured data for the article
  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: post.title,
    description: post.excerpt,
    image: (post.images?.map((img) => `https://www.hayatinteriors.com${img.url}`)) ?? [
      `https://www.hayatinteriors.com${post.image}`,
    ],
    datePublished: post.publishDate,
    dateModified: post.publishDate,
    author: {
      '@type': 'Person',
      name: post.author.name,
      description: post.author.bio,
      image: post.author.image,
    },
    publisher: {
      '@type': 'Organization',
      name: 'Hayat Interiors',
      logo: {
        '@type': 'ImageObject',
        url: 'https://www.hayatinteriors.com/white-logo.png',
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': canonicalUrl,
    },
    keywords: post.tags.join(', '),
    articleSection: post.category,
  };

  const faqSchema =
    post.faqs && post.faqs.length > 0
      ? {
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: post.faqs.map((faq) => ({
            '@type': 'Question',
            name: faq.question,
            acceptedAnswer: {
              '@type': 'Answer',
              text: faq.answer,
            },
          })),
        }
      : null;

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: 'https://www.hayatinteriors.com',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Blog',
        item: 'https://www.hayatinteriors.com/blog',
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: post.title,
        item: canonicalUrl,
      },
    ],
  };

  const localBusinessSchema = {
    '@context': 'https://schema.org',
    ...localBusiness,
  };

  const imageObjectSchemas =
    post.images?.map((img) => ({
      '@context': 'https://schema.org',
      '@type': 'ImageObject',
      contentUrl: `https://www.hayatinteriors.com${img.url}`,
      url: `https://www.hayatinteriors.com${img.url}`,
      description: img.alt,
    })) ?? [];

  return (
    <div className={styles.hayat}>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      {faqSchema && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
        />
      )}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(localBusinessSchema) }}
      />
      {imageObjectSchemas.map((schema, index) => (
        <script
          key={schema.url ?? index}
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
        />
      ))}
      <Header />
      
      <article className="min-h-screen bg-white">
        {/* Hero Section */}
        <div className="relative h-[60vh] min-h-[500px] bg-black">
          <AppImage
            src={post.image}
            alt={post.alt}
            fill
            className="object-cover opacity-60"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
          
          <div className="absolute bottom-0 left-0 right-0 px-4 md:px-8 lg:px-20 pb-12 md:pb-16">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center gap-3 mb-4 md:mb-6">
                <span className="px-4 py-2 bg-[#1B5E4F] text-white text-sm font-medium rounded-full">
                  {post.category}
                </span>
                <span className="text-white/80 text-sm">{post.readTime}</span>
              </div>
              <h1 className="font-headline text-3xl md:text-4xl lg:text-6xl font-normal text-white mb-4 md:mb-6 tracking-tight leading-tight">
                {post.title}
              </h1>
              <p className="text-lg md:text-xl text-white/90 font-normal max-w-3xl">
                {post.excerpt}
              </p>
            </div>
          </div>
        </div>

        {/* Author Info & Date */}
        <div className="border-b border-zinc-200">
          <div className="max-w-4xl mx-auto px-4 md:px-8 lg:px-20 py-6 md:py-8">
            <div className="flex items-center justify-between flex-wrap gap-6">
              <div className="flex items-center gap-4">
                <div className="relative w-16 h-16 rounded-full overflow-hidden ring-2 ring-[#1B5E4F]/20">
                  <AppImage
                    src={post.author.image}
                    alt={post.author.alt}
                    fill
                    className="object-cover"
                  />
                </div>
                <div>
                  <p className="font-medium text-black text-lg">
                    {post.author.name}
                  </p>
                  <p className="text-sm text-black/60 font-normal">
                    {post.author.bio}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2 text-black/60">
                <Icon name="CalendarIcon" size={20} />
                <span className="text-sm font-normal">
                  {formatDate(post.publishDate)}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Article Content */}
        <div className="max-w-4xl mx-auto px-4 md:px-8 lg:px-20 py-12 md:py-16">
          <div 
            className="prose prose-lg max-w-none
              prose-headings:font-headline 
              prose-headings:font-normal 
              prose-headings:text-black
              prose-h2:text-4xl 
              prose-h2:mt-16 
              prose-h2:mb-6
              prose-h3:text-2xl 
              prose-h3:mt-12 
              prose-h3:mb-4
              prose-h4:text-xl
              prose-h4:mt-8
              prose-h4:mb-3
              prose-p:text-black/80 
              prose-p:font-normal 
              prose-p:leading-relaxed
              prose-p:mb-6
              prose-ul:my-6
              prose-li:text-black/80
              prose-li:font-normal
              prose-li:mb-2
              prose-strong:text-black
              prose-strong:font-medium
              prose-a:text-[#1B5E4F]
              prose-a:no-underline
              hover:prose-a:underline"
            dangerouslySetInnerHTML={renderContent()}
          />

          {/* Tags */}
          <div className="mt-16 pt-8 border-t border-zinc-200">
            <div className="flex flex-wrap gap-3">
              {post.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-4 py-2 bg-zinc-100 text-black/70 text-sm rounded-full font-normal hover:bg-zinc-200 transition-colors duration-200"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>

          {/* Share Section */}
          <div className="mt-12 pt-8 border-t border-zinc-200">
            <h3 className="font-headline text-2xl font-normal text-black mb-4">
              Share this article
            </h3>
            <div className="flex gap-4">
              <button
                onClick={() => {
                  if (navigator.share) {
                    navigator.share({
                      title: post.title,
                      text: post.excerpt,
                      url: window.location.href,
                    });
                  }
                }}
                className="flex items-center gap-2 px-6 py-3 bg-[#1B5E4F] text-white rounded-full hover:bg-[#1B5E4F]/90 transition-colors duration-200"
              >
                <Icon name="ShareIcon" size={20} />
                <span className="text-sm font-medium">Share</span>
              </button>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(window.location.href);
                  alert('Link copied to clipboard!');
                }}
                className="flex items-center gap-2 px-6 py-3 border border-zinc-300 text-black rounded-full hover:bg-zinc-50 transition-colors duration-200"
              >
                <Icon name="LinkIcon" size={20} />
                <span className="text-sm font-medium">Copy Link</span>
              </button>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="bg-zinc-50 border-t border-zinc-200">
          <div className="max-w-4xl mx-auto px-4 md:px-8 lg:px-20 py-12 md:py-16 text-center">
            <h3 className="font-headline text-2xl md:text-3xl lg:text-4xl font-normal text-black mb-4">
              Ready to Transform Your Home?
            </h3>
            <p className="text-lg text-black/70 font-normal mb-8 max-w-2xl mx-auto">
              Let's discuss how Hayat Interiors can create a space that blends modern minimalism with timeless Indian craftsmanship for your home.
            </p>
            <Link
              href="/contact"
              className="inline-flex items-center gap-2 px-8 py-4 bg-[#1B5E4F] text-white rounded-full hover:bg-[#1B5E4F]/90 transition-colors duration-200"
            >
              <span className="font-medium">Schedule a Consultation</span>
              <Icon name="ArrowRightIcon" size={20} />
            </Link>
          </div>
        </div>

        {/* Back to Blog */}
        <div className="max-w-4xl mx-auto px-8 lg:px-20 py-12">
          <Link
            href="/blog"
            className="inline-flex items-center gap-2 text-[#1B5E4F] hover:gap-3 transition-all duration-200"
          >
            <Icon name="ArrowLeftIcon" size={20} />
            <span className="font-medium">Back to All Articles</span>
          </Link>
        </div>
      </article>

      <Footer />
    </div>
  );
};

export default BlogArticle;
